import { GUIDE_DOTS } from '@/app/guide/guideDot';
import AppIcon from '@/components/AppIcon';
import { DraftBadge, PublishedBadge } from '@/components/badges/Common';
import { PageHeader, PageShell } from '@/components/PageShell';
import { StatusDot } from '@/components/StatusDot';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { sortSpacesDraftsLast } from '@/lib/spaceOrder';
import { equipmentTypesState, infoTypesState, spacesState } from '@/state';
import { draftFlowsState } from '@/state/draftFlows.state';
import { guideHealthState } from '@/state/guide.state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { KEY_INFO_TITLE, type Space, SpaceGroupLabels, type SpaceId } from '@rock/types';
import { ChevronRight } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { Link } from 'react-router-dom';

/**
 * Every space at once — what the left nav can't show, since it lists them one name at a time.
 *
 * The column that earns the page is Guides: a space's guide can be live and current, or live with
 * changes still sitting in a draft, and the only way to tell used to be visiting each space in turn.
 */
export const SpacesListPage: FC = () => {
    const [spaces, spacesLoadable] = useLoadableAtomValue(spacesState, []);
    const onRowClick = useRowClick();
    const rows = useMemo(() => sortSpacesDraftsLast(spaces), [spaces]);

    return (
        <PageShell>
            <PageHeader
                title="Spaces"
                description="The spaces the app groups a home into, each with its key information, equipment types and onboarding guides."
            >
                <Button asChild className="cursor-pointer">
                    <Link to="/spaces/new">Add Space</Link>
                </Button>
            </PageHeader>

            {spacesLoadable.state !== 'hasData' ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : (
                <Table className="not-prose">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Space</TableHead>
                            <TableHead>ID</TableHead>
                            <TableHead>Group</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>{KEY_INFO_TITLE}</TableHead>
                            <TableHead>Equipment Types</TableHead>
                            <TableHead>Guides</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((space) => (
                            <TableRow
                                key={space.id}
                                className="hover:bg-muted cursor-pointer"
                                onClick={onRowClick(`/spaces/${space.id}`)}
                            >
                                <TableCell>
                                    <div className="flex flex-row items-center gap-2 font-medium">
                                        {space.icon && <AppIcon icon={space.icon} />}
                                        {space.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground">{space.systemTitle}</div>
                                </TableCell>
                                <TableCell className="text-muted-foreground">{space.id}</TableCell>
                                <TableCell className="text-muted-foreground">
                                    {SpaceGroupLabels[space.groupId] ?? space.groupId}
                                </TableCell>
                                <TableCell>{space.published ? <PublishedBadge /> : <DraftBadge />}</TableCell>
                                <TableCell>
                                    <InfoTypeCount spaceId={space.id} />
                                </TableCell>
                                <TableCell>
                                    <EquipmentTypeCount spaceId={space.id} />
                                </TableCell>
                                <TableCell>
                                    <SpaceGuides space={space} />
                                </TableCell>
                                {/* The whole row opens the space; the carat is just where that's headed. */}
                                <TableCell className="text-right">
                                    <ChevronRight className="ml-auto size-4 text-muted-foreground" />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </PageShell>
    );
};

/**
 * How many guides the space has, led by the same status dot its Guides tab shows — so a space with
 * changes waiting to be published is visible from here without opening it.
 */
const SpaceGuides: FC<{ space: Space }> = ({ space }) => {
    // Stable object — the flow atom families key on it, so a fresh literal each render thrashes them.
    const params = useMemo(() => ({ spaceId: space.id, equipmentTypeId: undefined }), [space.id]);
    const guides = useAsyncAtomValue(draftFlowsState(params), []);
    const health = useAsyncAtomValue(guideHealthState(params), 'none');

    if (guides.length === 0) return <span className="text-muted-foreground">-</span>;
    return (
        <div className="flex flex-row items-center gap-2">
            <StatusDot dot={GUIDE_DOTS[health]} />
            {guides.length}
        </div>
    );
};

// Both counts derive from one top-level subscription apiece (see itemTypes.state), so a row costs
// nothing beyond the filter.
const InfoTypeCount: FC<{ spaceId: SpaceId }> = ({ spaceId }) => {
    const infoTypes = useAsyncAtomValue(infoTypesState(spaceId), []);
    return <Count value={infoTypes.length} />;
};

const EquipmentTypeCount: FC<{ spaceId: SpaceId }> = ({ spaceId }) => {
    const types = useAsyncAtomValue(equipmentTypesState(spaceId), []);
    return <Count value={types.length} />;
};

const Count: FC<{ value: number }> = ({ value }) =>
    value === 0 ? <span className="text-muted-foreground">-</span> : <span>{value}</span>;
